import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PDFViewer from "../components/PDFViewer";
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
//import './SoftSkillsPage.css';
import { ReactComponent as Icon1 } from '../assets/images/softskills1.svg';
import { ReactComponent as Icon2 } from '../assets/images/softskills2.svg';
import { ReactComponent as Icon3 } from '../assets/images/softskills3.svg';
import { generatePDF } from "../components/generatePDF";
import useNavigationWarning from '../hooks/useNavigationWarning';


const SoftSkillsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [resumeData, setResumeData] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [expandedRoles, setExpandedRoles] = useState({});

  const {
    resumeData: initialResumeData,
    metrics,
    similarJobTitles,
    softSkills = []
  } = location.state || {};

  // Warn users before leaving while editing soft skills (allow forward to results)
  useNavigationWarning(true, 'You haven\'t finished adding soft skills. Are you sure you want to leave?', {
    allowPaths: ['/results'],
  });

  useEffect(() => {
    // Add a style tag directly to the document head
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .app-container {
        background: linear-gradient(180deg, #FFFFFF 0%, #F0FBF3 35%, #E2F5E0 100%) !important;
        background-image: linear-gradient(180deg, #FFFFFF 0%, #F0FBF3 35%, #E2F5E0 100%) !important;
        background-attachment: fixed !important;
      }
      
      .app-container::before,
      .app-container::after {
        display: none !important;
      }
    `;
    document.head.appendChild(styleElement);

    // Clean up when component unmounts
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const handleGeneratePDF = async (data) => {
    // Filter out omitted items before generating PDF
    const preparedData = prepareDataForPDF(data);
    return generatePDF(preparedData, setPdfData);
  };

  const deepCopy = (obj) => {
    return JSON.parse(JSON.stringify(obj));
  };

  // filter out omitted items before generating PDF
  const prepareDataForPDF = (data) => {
    if (!data) return data;

    const filteredData = { ...data };

    // Filter out omitted companies
    if (filteredData.companies) {
      filteredData.companies = filteredData.companies
        .filter(company => !company.omitted)
        .map(company => ({
          ...company,
          roles: company.roles
            ? company.roles
              .filter(role => !role.omitted)
              .map(role => ({
                ...role,
                bullets: role.bullets
                  ? role.bullets
                    .map(bullet => {
                      if (typeof bullet === 'string') {
                        return bullet;
                      } else if (bullet && !bullet.omitted) {
                        return bullet.text || '';
                      }
                      return '';
                    })
                    .filter(bullet => bullet.trim() !== '')
                  : []
              }))
            : []
        }));
    }

    // Filter out omitted projects
    if (filteredData.projects) {
      filteredData.projects = filteredData.projects.filter(project => !project.omitted);
    }

    return filteredData;
  };
  // Store the initial data in a ref to preserve it
  const initialDataRef = React.useRef(null);
  // Initialize resume data from location state
  useEffect(() => {
    if (initialResumeData) {

      // Store a deep copy of the initial data
      if (!initialDataRef.current) {
        initialDataRef.current = deepCopy(initialResumeData);
      }
      setResumeData(initialResumeData);
      // Generate PDF from resume data
      handleGeneratePDF(initialResumeData);

      // Auto-expand the first company
      if (initialResumeData.companies && initialResumeData.companies.length > 0) {
        setExpandedCompanies({ 0: true });

        // Auto-expand the first role of the first company
        if (initialResumeData.companies[0].roles && initialResumeData.companies[0].roles.length > 0) {
          setExpandedRoles({ '0-0': true });
        }
      }
    }
  }, [initialResumeData]);

  if (!resumeData) {
    navigate('/');
    return null;
  }

  const handleSkip = () => {
    navigate('/results', {
      state: {
        resumeData,
        metrics,
        similarJobTitles
      }
    });
  };

  const handleAddSoftSkills = () => {
    setIsEditMode(true);
  };

  const toggleCompany = (companyIndex) => {
    setExpandedCompanies({
      ...expandedCompanies,
      [companyIndex]: !expandedCompanies[companyIndex]
    });
  };

  const toggleRole = (companyIndex, roleIndex) => {
    const roleKey = `${companyIndex}-${roleIndex}`;
    setExpandedRoles({
      ...expandedRoles,
      [roleKey]: !expandedRoles[roleKey]
    });
  };

  const handleCompanyChange = (companyIndex, field, value) => {
    const updatedCompanies = [...resumeData.companies];
    updatedCompanies[companyIndex] = {
      ...updatedCompanies[companyIndex],
      [field]: value
    };

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);
    // Regenerate PDF with updated data
    handleGeneratePDF(updatedResumeData);
  };



  const handleBulletChange = (companyIndex, roleIndex, bulletIndex, value) => {
    const updatedCompanies = [...resumeData.companies];

    // Check if the bullet is a string or object
    if (typeof updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex] === 'string') {
      updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex] = value;
    } else {
      // Update the text property
      updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex] = {
        ...updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex],
        text: value
      };
    }

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);
    handleGeneratePDF(updatedResumeData);
  };
  // Function to add a bullet to a role
  const handleAddBullet = (companyIndex, roleIndex) => {
    const updatedCompanies = [...resumeData.companies];

    if (!updatedCompanies[companyIndex].roles[roleIndex].bullets) {
      updatedCompanies[companyIndex].roles[roleIndex].bullets = [];
    }

    updatedCompanies[companyIndex].roles[roleIndex].bullets.push('');

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);
    handleGeneratePDF(updatedResumeData);
  };

  // Function to remove a bullet from a role
  const handleRemoveBullet = (companyIndex, roleIndex, bulletIndex) => {
    const updatedCompanies = [...resumeData.companies];
    updatedCompanies[companyIndex].roles[roleIndex].bullets.splice(bulletIndex, 1);

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);
    handleGeneratePDF(updatedResumeData);
  };

  // Add these functions after the handleMoveRoleDown function (around line 719)

  // Function to move a bullet up within a role
  const handleMoveBulletUp = (companyIndex, roleIndex, bulletIndex) => {
    if (bulletIndex === 0) return; // Can't move up if already at the top

    const updatedCompanies = [...resumeData.companies];
    const temp = updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex];
    updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex] = updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex - 1];
    updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex - 1] = temp;

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);
    handleGeneratePDF(updatedResumeData);
  };

  // Function to move a bullet down within a role
  const handleMoveBulletDown = (companyIndex, roleIndex, bulletIndex) => {
    const bullets = resumeData.companies[companyIndex].roles[roleIndex].bullets;
    if (bulletIndex === bullets.length - 1) return; // Can't move down if already at the bottom

    const updatedCompanies = [...resumeData.companies];
    const temp = updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex];
    updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex] = updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex + 1];
    updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex + 1] = temp;

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);
    handleGeneratePDF(updatedResumeData);
  };
  // Update the handleSubmit function in SoftSkillsPage.js
  const handleSubmit = () => {
    const normalizedData = normalizeResumeData(resumeData);

    navigate('/results', {
      state: {
        resumeData: normalizedData,
        metrics,
        similarJobTitles
      }
    });
  };

  // Count soft skill opportunities per company
  const countSoftSkillOpportunities = (company) => {
    let count = 0;
    if (company.roles) {
      company.roles.forEach(role => {
        if (role.bullets) {
          count += role.bullets.length;
        }
      });
    }
    return count;
  };

  // Chevron icon component (SVG) - copied from ResumeEditForm
  const ChevronIcon = ({ expanded }) => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
        transition: 'transform 0.3s ease'
      }}
    >
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  );
  const handleAddRole = (companyIndex) => {
    const updatedCompanies = [...resumeData.companies];

    if (!updatedCompanies[companyIndex].roles) {
      updatedCompanies[companyIndex].roles = [];
    }

    updatedCompanies[companyIndex].roles.push({
      title: '', // Leave title blank instead of auto-generating
      bullets: ['']
    });

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);

    // Auto-expand the new role
    const newRoleIndex = updatedCompanies[companyIndex].roles.length - 1;
    const roleKey = `${companyIndex}-${newRoleIndex}`;
    setExpandedRoles({
      ...expandedRoles,
      [roleKey]: true
    });

    handleGeneratePDF(updatedResumeData);
  };
  const handleRemoveRole = (companyIndex, roleIndex) => {
    const updatedCompanies = [...resumeData.companies];

    // Remove the role at the specified index
    updatedCompanies[companyIndex].roles.splice(roleIndex, 1);

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);

    // Clean up expandedRoles state to avoid references to deleted roles
    const newExpandedRoles = { ...expandedRoles };
    Object.keys(newExpandedRoles).forEach(key => {
      const [compIdx, roleIdx] = key.split('-').map(Number);
      if (compIdx === companyIndex && roleIdx >= roleIndex) {
        delete newExpandedRoles[key];
      }
    });

    setExpandedRoles(newExpandedRoles);

    // Regenerate PDF with updated data
    handleGeneratePDF(updatedResumeData);
  };
  const handleRoleChange = (companyIndex, roleIndex, field, value) => {
    const updatedCompanies = [...resumeData.companies];

    if (!updatedCompanies[companyIndex].roles[roleIndex]) {
      updatedCompanies[companyIndex].roles[roleIndex] = {};
    }

    updatedCompanies[companyIndex].roles[roleIndex] = {
      ...updatedCompanies[companyIndex].roles[roleIndex],
      [field]: value
    };

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);

    // Regenerate PDF with updated data
    handleGeneratePDF(updatedResumeData);
  };

  // Update the handleAddCompany function:
  const handleAddCompany = () => {
    const updatedCompanies = [...resumeData.companies, {
      name: '', // Leave name blank instead of auto-generating
      location: '',
      roles: [{
        title: '', // Leave title blank instead of auto-generating
        start_year: '',
        end_year: '',
        bullets: ['']
      }]
    }];

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);

    // Auto-expand the new company
    const newCompanyIndex = updatedCompanies.length - 1;
    setExpandedCompanies({
      ...expandedCompanies,
      [newCompanyIndex]: true
    });

    // Auto-expand the first role of the new company
    setExpandedRoles({
      ...expandedRoles,
      [`${newCompanyIndex}-0`]: true
    });

    handleGeneratePDF(updatedResumeData);
  };
  // Replace handleRemoveCompany with handleToggleCompany
  const handleToggleCompany = (companyIndex) => {
    const updatedCompanies = [...resumeData.companies];

    // Toggle the omitted property
    updatedCompanies[companyIndex] = {
      ...updatedCompanies[companyIndex],
      omitted: !updatedCompanies[companyIndex].omitted
    };

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);
    handleGeneratePDF(updatedResumeData);
  };

  // Replace handleRemoveRole with handleToggleRole
  const handleToggleRole = (companyIndex, roleIndex) => {
    const updatedCompanies = [...resumeData.companies];

    // Toggle the omitted property
    updatedCompanies[companyIndex].roles[roleIndex] = {
      ...updatedCompanies[companyIndex].roles[roleIndex],
      omitted: !updatedCompanies[companyIndex].roles[roleIndex].omitted
    };

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);
    handleGeneratePDF(updatedResumeData);
  };

  // Replace handleRemoveBullet with handleToggleBullet
  const handleToggleBullet = (companyIndex, roleIndex, bulletIndex) => {
    const updatedCompanies = [...resumeData.companies];

    // For bullets, we'll use an array of objects instead of strings
    if (typeof updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex] === 'string') {
      // Convert the string to an object with text and omitted properties
      const bulletText = updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex];
      updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex] = {
        text: bulletText,
        omitted: true // Start as omitted since we're toggling
      };
    } else {
      // Toggle the omitted property
      updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex] = {
        ...updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex],
        omitted: !updatedCompanies[companyIndex].roles[roleIndex].bullets[bulletIndex].omitted
      };
    }

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);
    handleGeneratePDF(updatedResumeData);
  };
  // For companies
  const handleDeleteCompany = (companyIndex) => {
    const updatedCompanies = [...resumeData.companies];
    updatedCompanies.splice(companyIndex, 1);

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);

    // Clean up expandedCompanies and expandedRoles state
    const newExpandedCompanies = { ...expandedCompanies };
    const newExpandedRoles = { ...expandedRoles };

    Object.keys(newExpandedCompanies).forEach(idx => {
      const index = Number(idx);
      if (index === companyIndex) {
        delete newExpandedCompanies[idx];
      } else if (index > companyIndex) {
        newExpandedCompanies[index - 1] = newExpandedCompanies[idx];
        delete newExpandedCompanies[idx];
      }
    });

    Object.keys(newExpandedRoles).forEach(key => {
      const [compIdx, roleIdx] = key.split('-').map(Number);
      if (compIdx === companyIndex) {
        delete newExpandedRoles[key];
      } else if (compIdx > companyIndex) {
        newExpandedRoles[`${compIdx - 1}-${roleIdx}`] = newExpandedRoles[key];
        delete newExpandedRoles[key];
      }
    });

    setExpandedCompanies(newExpandedCompanies);
    setExpandedRoles(newExpandedRoles);

    handleGeneratePDF(updatedResumeData);
  };

  // For roles
  const handleDeleteRole = (companyIndex, roleIndex) => {
    const updatedCompanies = [...resumeData.companies];
    updatedCompanies[companyIndex].roles.splice(roleIndex, 1);

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    setResumeData(updatedResumeData);

    // Clean up expandedRoles state
    const newExpandedRoles = { ...expandedRoles };
    Object.keys(newExpandedRoles).forEach(key => {
      const [compIdx, roleIdx] = key.split('-').map(Number);
      if (compIdx === companyIndex && roleIdx === roleIndex) {
        delete newExpandedRoles[key];
      } else if (compIdx === companyIndex && roleIdx > roleIndex) {
        newExpandedRoles[`${compIdx}-${roleIdx - 1}`] = newExpandedRoles[key];
        delete newExpandedRoles[key];
      }
    });

    setExpandedRoles(newExpandedRoles);
    handleGeneratePDF(updatedResumeData);
  };

  // Update the handleDeleteBullet function to include a confirmation
  const handleDeleteBullet = (companyIndex, roleIndex, bulletIndex) => {
    // Add confirmation dialog
    if (window.confirm("Are you sure you want to delete this responsibility?")) {
      const updatedCompanies = [...resumeData.companies];
      updatedCompanies[companyIndex].roles[roleIndex].bullets.splice(bulletIndex, 1);

      const updatedResumeData = {
        ...resumeData,
        companies: updatedCompanies
      };

      setResumeData(updatedResumeData);
      handleGeneratePDF(updatedResumeData);
    }
  };
  // Update the reset handler to use the stored initial data
  const handleReset = () => {
    if (window.confirm("Are you sure you want to reset all your changes?")) {
      // Reset to initial data from the ref
      if (initialDataRef.current) {
        // Create a fresh deep copy to avoid reference issues
        const freshCopy = deepCopy(initialDataRef.current);
        setResumeData(freshCopy);
        handleGeneratePDF(freshCopy);

        // Reset expansion state as well
        if (freshCopy.companies && freshCopy.companies.length > 0) {
          setExpandedCompanies({ 0: true });

          if (freshCopy.companies[0].roles && freshCopy.companies[0].roles.length > 0) {
            setExpandedRoles({ '0-0': true });
          } else {
            setExpandedRoles({});
          }
        } else {
          setExpandedCompanies({});
          setExpandedRoles({});
        }
      }
    }
  };
  // Add these functions to SoftSkillsPage.js

  // Function to move a company up in the list
  const handleMoveCompanyUp = (companyIndex) => {
    if (companyIndex === 0) return; // Can't move up if already at the top

    const updatedCompanies = [...resumeData.companies];
    const temp = updatedCompanies[companyIndex];
    updatedCompanies[companyIndex] = updatedCompanies[companyIndex - 1];
    updatedCompanies[companyIndex - 1] = temp;

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    // Update expanded states
    const newExpandedCompanies = {};
    Object.keys(expandedCompanies).forEach(idx => {
      const index = Number(idx);
      if (index === companyIndex) {
        newExpandedCompanies[index - 1] = expandedCompanies[idx];
      } else if (index === companyIndex - 1) {
        newExpandedCompanies[index + 1] = expandedCompanies[idx];
      } else {
        newExpandedCompanies[index] = expandedCompanies[idx];
      }
    });

    const newExpandedRoles = {};
    Object.keys(expandedRoles).forEach(key => {
      const [compIdx, roleIdx] = key.split('-').map(Number);
      if (compIdx === companyIndex) {
        newExpandedRoles[`${compIdx - 1}-${roleIdx}`] = expandedRoles[key];
      } else if (compIdx === companyIndex - 1) {
        newExpandedRoles[`${compIdx + 1}-${roleIdx}`] = expandedRoles[key];
      } else {
        newExpandedRoles[key] = expandedRoles[key];
      }
    });

    setExpandedCompanies(newExpandedCompanies);
    setExpandedRoles(newExpandedRoles);
    setResumeData(updatedResumeData);
    handleGeneratePDF(updatedResumeData);
  };

  // Function to move a company down in the list
  const handleMoveCompanyDown = (companyIndex) => {
    if (companyIndex === resumeData.companies.length - 1) return; // Can't move down if already at the bottom

    const updatedCompanies = [...resumeData.companies];
    const temp = updatedCompanies[companyIndex];
    updatedCompanies[companyIndex] = updatedCompanies[companyIndex + 1];
    updatedCompanies[companyIndex + 1] = temp;

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    // Update expanded states
    const newExpandedCompanies = {};
    Object.keys(expandedCompanies).forEach(idx => {
      const index = Number(idx);
      if (index === companyIndex) {
        newExpandedCompanies[index + 1] = expandedCompanies[idx];
      } else if (index === companyIndex + 1) {
        newExpandedCompanies[index - 1] = expandedCompanies[idx];
      } else {
        newExpandedCompanies[index] = expandedCompanies[idx];
      }
    });

    const newExpandedRoles = {};
    Object.keys(expandedRoles).forEach(key => {
      const [compIdx, roleIdx] = key.split('-').map(Number);
      if (compIdx === companyIndex) {
        newExpandedRoles[`${compIdx + 1}-${roleIdx}`] = expandedRoles[key];
      } else if (compIdx === companyIndex + 1) {
        newExpandedRoles[`${compIdx - 1}-${roleIdx}`] = expandedRoles[key];
      } else {
        newExpandedRoles[key] = expandedRoles[key];
      }
    });

    setExpandedCompanies(newExpandedCompanies);
    setExpandedRoles(newExpandedRoles);
    setResumeData(updatedResumeData);
    handleGeneratePDF(updatedResumeData);
  };

  // Function to move a role up within a company
  const handleMoveRoleUp = (companyIndex, roleIndex) => {
    if (roleIndex === 0) return; // Can't move up if already at the top

    const updatedCompanies = [...resumeData.companies];
    const temp = updatedCompanies[companyIndex].roles[roleIndex];
    updatedCompanies[companyIndex].roles[roleIndex] = updatedCompanies[companyIndex].roles[roleIndex - 1];
    updatedCompanies[companyIndex].roles[roleIndex - 1] = temp;

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    // Update expanded roles state
    const newExpandedRoles = { ...expandedRoles };
    const currentKey = `${companyIndex}-${roleIndex}`;
    const previousKey = `${companyIndex}-${roleIndex - 1}`;

    const currentExpanded = expandedRoles[currentKey];
    const previousExpanded = expandedRoles[previousKey];

    if (currentExpanded !== undefined) {
      newExpandedRoles[previousKey] = currentExpanded;
      delete newExpandedRoles[currentKey];
    }

    if (previousExpanded !== undefined) {
      newExpandedRoles[currentKey] = previousExpanded;
      delete newExpandedRoles[previousKey];
    }

    setExpandedRoles(newExpandedRoles);
    setResumeData(updatedResumeData);
    handleGeneratePDF(updatedResumeData);
  };

  // Function to move a role down within a company
  const handleMoveRoleDown = (companyIndex, roleIndex) => {
    if (roleIndex === resumeData.companies[companyIndex].roles.length - 1) return; // Can't move down if already at the bottom

    const updatedCompanies = [...resumeData.companies];
    const temp = updatedCompanies[companyIndex].roles[roleIndex];
    updatedCompanies[companyIndex].roles[roleIndex] = updatedCompanies[companyIndex].roles[roleIndex + 1];
    updatedCompanies[companyIndex].roles[roleIndex + 1] = temp;

    const updatedResumeData = {
      ...resumeData,
      companies: updatedCompanies
    };

    // Update expanded roles state
    const newExpandedRoles = { ...expandedRoles };
    const currentKey = `${companyIndex}-${roleIndex}`;
    const nextKey = `${companyIndex}-${roleIndex + 1}`;

    const currentExpanded = expandedRoles[currentKey];
    const nextExpanded = expandedRoles[nextKey];

    if (currentExpanded !== undefined) {
      newExpandedRoles[nextKey] = currentExpanded;
      delete newExpandedRoles[currentKey];
    }

    if (nextExpanded !== undefined) {
      newExpandedRoles[currentKey] = nextExpanded;
      delete newExpandedRoles[nextKey];
    }

    setExpandedRoles(newExpandedRoles);
    setResumeData(updatedResumeData);
    handleGeneratePDF(updatedResumeData);
  };

  // Add this function to SoftSkillsPage.js
  const normalizeResumeData = (data) => {
    const normalizedData = { ...data };

    if (normalizedData.companies) {
      normalizedData.companies = normalizedData.companies.map(company => {
        const normalizedCompany = { ...company };

        if (normalizedCompany.roles) {
          normalizedCompany.roles = normalizedCompany.roles.map(role => {
            const normalizedRole = { ...role };

            if (normalizedRole.bullets) {
              normalizedRole.bullets = normalizedRole.bullets.map(bullet => {
                // Convert object bullets back to strings if they're not omitted
                if (typeof bullet === 'object') {
                  return bullet.omitted ? '' : bullet.text;
                }
                return bullet;
              }).filter(bullet => bullet.trim() !== ''); // Remove empty bullets
            }

            return normalizedRole;
          }).filter(role => !role.omitted); // Remove omitted roles
        }

        return normalizedCompany;
      }).filter(company => !company.omitted); // Remove omitted companies
    }

    return normalizedData;
  };
  return (
    <div className="soft-skills-page">
      <h1 className="centered-title">Add Soft Skills to Your Resume</h1>

      <div className="soft-skills-container">
        {pdfData && (
          <>
            {!isEditMode ? (
              // State 1: Display soft skills with tips
              <>
                <div className="pdf-viewer-container">
                  <PDFViewer pdfData={pdfData} />
                </div>
                <div className="soft-skills-info">
                  <div className="soft-skills-card">
                  <div className="card-header">
                      <div className="card-icon trophy">
                        <Icon3 />
                      </div>
                      <h3>Edit your resume to sprinkle in these soft skills for maximum impact</h3>
                    </div>
                    <p className="card-description-soft-skills">
                      Tip! If your resume doesn't already have these skills, add them in your work experience section.
                    </p>

                    <div className="card-header">
                      <div className="card-icon check">
                        <Icon1 />
                      </div>
                      <h3>The following soft skills were detected as being in demand</h3>
                    </div>
                    <div className="soft-skills-grid">
                      {softSkills.map((skillObj, index) => (
                        <div key={index} className="soft-skill-tag">
                          {skillObj.skill || skillObj}
                        </div>
                      ))}
                      {/* Add some placeholder skills if none are provided */}
                      {softSkills.length === 0 && (
                        <>
                          All of the soft skills are already in your resume
                        </>
                      )}
                    </div>

                    <div className="card-header">
                      <div className="card-icon fingerprint">
                        <Icon2 />
                      </div>
                      <h3>Soft skills are best exemplified throughout your experience</h3>
                    </div>
                    <p className="card-description-soft-skills">
                      For example, if you are a software engineer, you can add "Problem Solving" as a soft skill by adding an example of how you solved a problem in your work experience.
                    </p>


                    <div className="soft-skills-actions">
                      <button className="skip-button" onClick={handleSkip}>
                        Skip
                      </button>
                      <button className="add-soft-skills-button" onClick={handleAddSoftSkills}>
                        Add Soft Skills <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              // State 2: Edit work experience with accordion structure
              // Replace the edit mode section (around lines 836-924) with this:
              <>
                <div className="pdf-viewer-container">
                  <PDFViewer pdfData={pdfData} />
                  <div className="button-container">
                    <button
                      className="view-button"
                      onClick={() => setIsEditMode(false)}
                    >
                      Back to Overview
                    </button>
                  </div>
                </div>
                <div className="soft-skills-info">
                  <div className="work-experience-container">
                    <h2>Work Experience</h2>
                    <p>Edit your work Experience Section Below to add Soft Skills</p>

                    <div className="soft-skills-reference">
                      <h3>Soft Skills Reference</h3>
                      <p>Consider incorporating these soft skills into your bullet points:</p>
                      <div className="soft-skills-grid-edit">
                        {softSkills.map((skillObj, index) => (
                          <div key={index} className="soft-skill-tag reference">
                            {typeof skillObj === 'object' && skillObj.skill ? skillObj.skill : skillObj}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="form-section-content">
                      {resumeData.companies && resumeData.companies.map((company, companyIndex) => (
                        <div key={`company-${companyIndex}`} className="company-section" style={{ opacity: company.omitted ? 0.5 : 1 }}>
                          <div className="company-header">
                            <div className="company-header-left" onClick={() => toggleCompany(companyIndex)}>
                              <ChevronIcon expanded={expandedCompanies[companyIndex]} />
                              <h4>{company.name || 'Company'}</h4>
                            </div>
                            <div className="company-header-right">
                              <span className="company-badge">
                                {company.roles?.filter(r => !r.omitted).reduce((acc, role) =>
                                  acc + (role.bullets?.filter(b => typeof b === 'string' || !b.omitted).length || 0), 0) || 0}
                              </span>
                              <div className="action-buttons">
                                <button
                                  className="move-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveCompanyUp(companyIndex);
                                  }}
                                  title="Move company up"
                                  disabled={companyIndex === 0}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </button>
                                <button
                                  className="move-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMoveCompanyDown(companyIndex);
                                  }}
                                  title="Move company down"
                                  disabled={companyIndex === resumeData.companies.length - 1}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </button>
                                <div className="toggle-container">
                                  <label className="toggle-switch" title={company.omitted ? "Include in resume" : "Exclude from resume"}>
                                    <input
                                      type="checkbox"
                                      checked={!company.omitted}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        handleToggleCompany(companyIndex);
                                      }}
                                    />
                                    <span className="slider"></span>
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>

                          {expandedCompanies[companyIndex] && (
                            <div className="company-details">
                              <div className="form-group">
                                <label>Company Name</label>
                                <input
                                  type="text"
                                  value={company.name || ''}
                                  onChange={e => handleCompanyChange(companyIndex, 'name', e.target.value)}
                                  placeholder="Company Name"
                                  className="form-input"
                                />
                              </div>

                              <div className="form-group">
                                <label>Location</label>
                                <input
                                  type="text"
                                  value={company.location || ''}
                                  onChange={e => handleCompanyChange(companyIndex, 'location', e.target.value)}
                                  placeholder="City, State"
                                  className="form-input"
                                />
                              </div>

                              {/* <div className="form-row">
                                <div className="form-group half-width">
                                  <label>Start Year</label>
                                  <input
                                    type="text"
                                    value={company.start_year || ''}
                                    onChange={e => handleCompanyChange(companyIndex, 'start_year', e.target.value)}
                                    placeholder="YYYY"
                                    className="form-input"
                                  />
                                </div>

                                <div className="form-group half-width">
                                  <label>End Year</label>
                                  <input
                                    type="text"
                                    value={company.end_year || ''}
                                    onChange={e => handleCompanyChange(companyIndex, 'end_year', e.target.value)}
                                    placeholder="YYYY or Present"
                                    className="form-input"
                                  />
                                </div>
                              </div> */}

                              <div className="form-group">
                                <label>Roles</label>
                                {company.roles && company.roles.map((role, roleIndex) => {
                                  const roleKey = `${companyIndex}-${roleIndex}`;
                                  return (
                                    <div key={`role-${roleKey}`} className="role-section">
                                      <div className="role-header">
                                        <div className="role-header-left" onClick={() => toggleRole(companyIndex, roleIndex)}>
                                          <ChevronIcon expanded={expandedRoles[roleKey]} />
                                          <h5>{role.title || ''}</h5>
                                        </div>
                                        <div className="role-header-right">
                                          <span className="role-badge">
                                            {role.bullets?.filter(b => typeof b === 'string' || !b.omitted).length || 0}
                                          </span>
                                          <div className="action-buttons">
                                            <button
                                              className="move-button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleMoveRoleUp(companyIndex, roleIndex);
                                              }}
                                              title="Move role up"
                                              disabled={roleIndex === 0}
                                            >
                                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              </svg>
                                            </button>
                                            <button
                                              className="move-button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleMoveRoleDown(companyIndex, roleIndex);
                                              }}
                                              title="Move role down"
                                              disabled={roleIndex === company.roles.length - 1}
                                            >
                                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                              </svg>
                                            </button>
                                            <div className="toggle-container">
                                              <label className="toggle-switch" title={role.omitted ? "Include in resume" : "Exclude from resume"}>
                                                <input
                                                  type="checkbox"
                                                  checked={!role.omitted}
                                                  onChange={(e) => {
                                                    e.stopPropagation();
                                                    handleToggleRole(companyIndex, roleIndex);
                                                  }}
                                                />
                                                <span className="slider"></span>
                                              </label>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      {expandedRoles[roleKey] && (
                                        <div className="role-details">
                                          <div className="form-group">
                                            <label>Role Title</label>
                                            <input
                                              type="text"
                                              value={role.title || ''}
                                              onChange={e => handleRoleChange(companyIndex, roleIndex, 'title', e.target.value)}
                                              placeholder="Role Title"
                                              className="form-input"
                                            />
                                          </div>

                                          <div className="form-row">
                                            <div className="form-group half-width">
                                              <label>Start Year</label>
                                              <input
                                                type="text"
                                                value={role.start_year || ''}
                                                onChange={e => handleRoleChange(companyIndex, roleIndex, 'start_year', e.target.value)}
                                                placeholder="YYYY"
                                                className="form-input"
                                              />
                                            </div>

                                            <div className="form-group half-width">
                                              <label>End Year</label>
                                              <input
                                                type="text"
                                                value={role.end_year || ''}
                                                onChange={e => handleRoleChange(companyIndex, roleIndex, 'end_year', e.target.value)}
                                                placeholder="YYYY or Present"
                                                className="form-input"
                                              />
                                            </div>
                                          </div>

                                          <div className="form-group">
                                            <label>Responsibilities:</label>
                                            {role.bullets && role.bullets.map((bullet, bulletIndex) => (
                                            <div key={`bullet-${companyIndex}-${roleIndex}-${bulletIndex}`} className="bullet-input-container">
                                            <div className="bullet-move-buttons">
                                              <button
                                                className="move-button"
                                                onClick={() => handleMoveBulletUp(companyIndex, roleIndex, bulletIndex)}
                                                title="Move responsibility up"
                                                disabled={bulletIndex === 0}
                                              >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                  <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                              </button>
                                              <button
                                                className="move-button"
                                                onClick={() => handleMoveBulletDown(companyIndex, roleIndex, bulletIndex)}
                                                title="Move responsibility down"
                                                disabled={bulletIndex === role.bullets.length - 1}
                                              >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                  <path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                              </button>
                                            </div>
                                            <textarea
                                              value={typeof bullet === 'string' ? bullet : bullet.text || ''}
                                              onChange={e => handleBulletChange(companyIndex, roleIndex, bulletIndex, e.target.value)}
                                              placeholder="Describe your responsibility or achievement"
                                              rows={2}
                                              className={typeof bullet === 'object' && bullet.omitted ? 'omitted' : ''}
                                            />
                                            <div className="toggle-container">
                                              <label className="toggle-switch" title={(typeof bullet === 'object' && bullet.omitted) ? "Include in resume" : "Exclude from resume"}>
                                                <input
                                                  type="checkbox"
                                                  checked={!(typeof bullet === 'object' && bullet.omitted)}
                                                  onChange={() => handleToggleBullet(companyIndex, roleIndex, bulletIndex)}
                                                />
                                                <span className="slider"></span>
                                              </label>  
                                                </div>
                                              </div>
                                            ))}

                                            <button
                                              className="add-bullet-button"
                                              onClick={() => handleAddBullet(companyIndex, roleIndex)}
                                            >
                                              <span className="plus-icon">+</span> Add Responsibility
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                <button className="add-role-button" onClick={() => handleAddRole(companyIndex)}>
                                  <span className="plus-icon">+</span> Add Role
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      <button className="add-company-button" onClick={handleAddCompany}>
                        <span className="plus-icon">+</span> Add Company
                      </button>
                    </div>

                    <div className="work-experience-actions">
                      <button className="skip-button" onClick={handleReset}>
                        Reset
                      </button>
                      <button className="done-button" onClick={handleSubmit}>
                        Done
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SoftSkillsPage;